import { booleanAttribute, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ITagIcon,
	ItemComponent,
	LinkComponent,
	ListDetailComponent,
} from '@tecalisdev/infinity';
import {
	CONNECTION_TYPE,
	EVALUATION_TYPE,
	IOperationFormDetail,
	OPERATION_STATUS,
} from '../../../../operations/models/operations.models';
import {
	buildFormMatchCompareTableRows,
	resolveFormMatchDataCheckStatus,
	resolveFormMatchDataLabelKey,
} from '../../../../operations/utils/form-operation-match-compare.util';
import { MasterService } from '../../../../shared/services/master.service';
import {
	buildDocumentsCompareTableRows,
	documentTypeLabelForCompare,
} from '../../../../shared/utils/documents-external-compare.util';
import {
	BOOLEAN_STATUS,
	CHECK,
	IOperationDocument,
	IProcessDetailResponse,
} from '../../../models/processes.models';
import { ProcessHelperService } from '../../../services/process-helper.service';

@Component({
	selector: 'app-checks-docs-eval',
	imports: [TranslatePipe, ItemComponent, ListDetailComponent, LinkComponent],
	templateUrl: './checks-docs-eval.component.html',
})
export class ChecksDocsEvalComponent {
	@Input() check: CHECK | undefined | null;
	@Input() detail: IProcessDetailResponse;
	@Input({ transform: booleanAttribute }) showOnlyKO = false;
	@Output() openDocumentsCompare = new EventEmitter<{ doc: IOperationDocument; event?: Event }>();
	@Output() openFormMatchModal = new EventEmitter<void>();

	public masterService = inject(MasterService);
	public processHelper = inject(ProcessHelperService);
	private trans = inject(TranslateService);

	public CHECK = CHECK;
	public OPERATION_STATUS = OPERATION_STATUS;
	public CONNECTION_TYPE = CONNECTION_TYPE;
	public EVALUATION_TYPE = EVALUATION_TYPE;

	public getTagIconItem(status: BOOLEAN_STATUS | string): ITagIcon {
		return status === 'OK'
			? { icon: 'icon-check', status: 'success', size: 'sm' }
			: { icon: 'icon-alert', status: 'error', size: 'sm' };
	}

	public buildDocumentsCompareTableRowsForDoc(doc: IOperationDocument | null) {
		return buildDocumentsCompareTableRows(doc, this.detail?.data?.DOCUMENTS ?? null, this.trans);
	}

	public documentCompareTypeLabel(doc: IOperationDocument): string {
		return documentTypeLabelForCompare(doc, this.masterService, this.trans);
	}

	protected shouldShowFormMatchData(): boolean {
		const form = this.detail?.data?.FORM as IOperationFormDetail | undefined;
		const status = resolveFormMatchDataCheckStatus(form);
		if (status === undefined) {
			return false;
		}
		return this.showOnlyKO ? status === BOOLEAN_STATUS.KO : true;
	}

	public get formMatchCompareRows() {
		const form = this.detail?.data?.FORM as IOperationFormDetail | undefined;
		if (!form) {
			return [];
		}
		return buildFormMatchCompareTableRows(
			form,
			{
				translate: (key) => this.trans.instant(key),
				hasTranslation: (key) => this.masterService.hasTranslation(key),
				locale: this.trans.currentLang,
			},
			undefined,
			this.detail?.data?.OCR ?? null,
		);
	}

	public get formMatchDataLabelKey(): string {
		const form = this.detail?.data?.FORM as IOperationFormDetail | undefined;
		return resolveFormMatchDataLabelKey(form, this.detail?.data?.OCR ?? null);
	}

	public get formMatchCheckStatus(): BOOLEAN_STATUS | undefined {
		return resolveFormMatchDataCheckStatus(this.detail?.data?.FORM as IOperationFormDetail | undefined);
	}
}
